import pick from 'lodash/pick';
import moment from 'moment';
import { useCallback } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { setError as setEditorError } from '../../store/EditorSlice';
import { ClipActor } from '../../../server/graphql/__generated.types';
import {
  useProductUsageLazyQuery,
  useProductUsageQuery,
} from '../../components/ProductUsage/operation.generated';
import useStudioClips from '../../components/StudioClipUserList/hooks/useStudioClips';
import TTSRequest from '../../services/TTSRequest';
import TTSRequestManager from '../../services/TTSRequestManager';
import generateSequence from '../../util/generateSequence';
import { captureExceptionWithState, isClientError } from '../../util/sentry';
import Editor from '../editor';
import { EventName, useEventsContext } from '../events/EventsContext';
import { CuesApplied } from '../../services/EventTrackingService';

export type UseTTSInputs = {
  clipId: string;
  projectId: string;
  actor: ClipActor;
  textInput: string; // plain text
  textFormat?: string; // formatted text
  renderMode: Editor.StoreState['renderMode'];
  // Used when rendering multiple items at once to keep clip index in proper ordering.
  indexOffset?: number;
  ttsVersion?: string;
  cues?: CuesApplied;
};

type UseTTSAction = (inputs: UseTTSInputs) => Promise<any>;

/**
 * Hook used for rendering text-to-speech. Please use `useClipCreator` hook in component/client
 * facing scenarios as it provides more logic around validation and errors while using this hook
 * internally. This hook provides a wrapper around the TTS request _and_ the ClipsContext in order
 * to send off the actual request and keep our local clips state in sync with the state of the
 * request. Errors from this request are propgated to the `ClipObjectPending` instance and handled
 * elsewhere.
 *
 * @example
 *  const [generateTTS] = useTTS()
 *  generateTTS({
 *    actor,
 *    textInput
 *  })
 *
 * @returns {[UseTTSAction]} The action returns a Promise that is resolved when the TTS headers are
 * received.
 */
function useTTS(projectId: string): [UseTTSAction] {
  const [refetchProductUsage] = useProductUsageLazyQuery({
    fetchPolicy: 'network-only', // force refetch from network
  });
  const { data: usageData } = useProductUsageQuery();
  const [{ clips }, { addPendingClip, updatePendingClip, deletePendingClip }] =
    useStudioClips(projectId);
  const [eventTracking] = useEventsContext();
  const appDispatch = useAppDispatch();

  const ttsAction = useCallback(
    (inputs: UseTTSInputs) => {
      return new Promise<void>(resolve => {
        let lastClipIndex = 0;
        if (clips.length > 0) {
          lastClipIndex = clips.sort((a, b) => (a.index > b.index ? 1 : -1))[
            clips.length - 1
          ].index;
        }

        const request = new TTSRequest({
          clipId: inputs.clipId,
          projectId: inputs.projectId,
          actor: {
            id: inputs.actor.id,
            variantId: inputs.actor.variant.id,
            version: inputs.ttsVersion ?? inputs.actor.variant.version,
            initials: inputs.actor.initials,
          },
          text: inputs.textInput,
          textFormat: inputs.textFormat,
        });
        // Temporary id generation. Clips are stored in {id} => {object} mapping so the following
        // should avoid conflicts. This temporary id will be overwritten once the clip is complete.
        let clipId = request.id;
        let isCreditableBy: Parameters<
          typeof addPendingClip
        >[0]['isCreditableBy'] = null;
        if (usageData?.user) {
          isCreditableBy = moment
            .unix(usageData.user.productAccessPeriodUsage.periodEnd)
            .valueOf()
            .toString();
        }
        addPendingClip({
          id: clipId,
          projectId: inputs.projectId,
          actorId: inputs.actor.id.toString(),
          actor: {
            __typename: 'ClipActor',
            ...pick(inputs.actor, [
              'id',
              'name',
              'image',
              'initials',
              'isCustomActor',
            ]),
            variant: inputs.actor.variant,
          },
          createdAt: moment().valueOf().toString(),
          isCreditableBy,
          streamRequest: request.stream,
          name: '',
          fileName: '',
          text: inputs.textInput,
          textFormat: inputs.textFormat ?? null,
          isComplete: false,
          index: lastClipIndex + 1 + (inputs.indexOffset || 0),
          sequence: generateSequence(),
          isDeleted: false,
          ttsVersion: inputs.ttsVersion,
        });
        let source: string = '';
        request.on('headersReceived', event => {
          source = event.detail.source;

          clipId = event.detail.id;
          // Update clips usage defined on the user query
          refetchProductUsage();
          resolve();
        });
        request.on('complete', () => {
          // Clip is complete and ready to play!
          // this method will replace the cached clip reference of the
          // generated clip id with the server clip id
          updatePendingClip(clipId, {
            isComplete: true,
            localSrc: request.stream.getObjectURL(),
          });
          eventTracking.track({
            type: EventName.ClipRender,
            data: {
              actorID: `${inputs.actor.id}`,
              actorVariantID: `${inputs.actor.variant.id}`,
              source,
              actorVariantType: inputs.actor.variant.variantType.id,
              actorName: inputs.actor.name,
              textLength: inputs.textInput.length,
              ttsVersion: inputs.ttsVersion ?? inputs.actor.variant.version,
              renderMode: inputs.renderMode,
              success: true,
              timeUntilHeadersReceived: request.timing.headersReceived,
              timeUntilRequestComplete: request.timing.requestComplete,
              cues: inputs.cues,
            },
          });
        });
        request.on('error', event => {
          if (
            event.detail.message.includes(
              'Please format your respelling correctly'
            )
          ) {
            // todo: replace with proper respelling link
            const text = event.detail.message.replace(
              '(help.wellsaidlabs.com/respellings)',
              '(https://help.wellsaid.io/hc/en-us/articles/39188339794451-Guide-to-Respellings#14-troubleshooting-respelling-errors)'
            );
            appDispatch(setEditorError(text));
            deletePendingClip(clipId);
          } else if (
            event.detail.message.includes(
              'Text cannot contain these characters:'
            )
          ) {
            appDispatch(setEditorError(event.detail.message));
            deletePendingClip(clipId);
          } else {
            updatePendingClip(clipId, {
              failedWithError: event.detail.message,
            });
          }
          resolve();
          eventTracking.track({
            type: EventName.ClipRender,
            data: {
              actorID: `${inputs.actor.id}`,
              actorVariantID: `${inputs.actor.variant.id}`,
              source,
              actorVariantType: inputs.actor.variant.variantType.id,
              actorName: inputs.actor.name,
              textLength: inputs.textInput.length,
              ttsVersion: inputs.ttsVersion ?? inputs.actor.variant.version,
              renderMode: inputs.renderMode,
              success: false,
              error: event.detail.message,
              cues: inputs.cues,
            },
          });
          if (!isClientError(request.stream.status)) {
            captureExceptionWithState(new Error(event.detail.message), {
              type: 'Clip',
              value: {
                actorId: `${inputs.actor.id}`,
                actorName: inputs.actor.name,
                textLength: inputs.textInput.length,
                ...(event.detail.debugContext ?? {}),
              },
            });
          }
        });
        TTSRequestManager.addRequest(request);
      });
    },
    [
      clips,
      usageData?.user,
      addPendingClip,
      refetchProductUsage,
      updatePendingClip,
      eventTracking,
      appDispatch,
      deletePendingClip,
    ]
  );

  return [ttsAction];
}

export default useTTS;
